import React, { useState } from 'react';
import {
  Box,
  Chip,
  TextField,
  Typography,
  Switch,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  alpha,
  Button,
  useTheme,
  FormControlLabel,
  Checkbox,
  Collapse,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FilterValue, FilterValueType, isScoreRange, isDate, ScoreRange, isStringArray, isDateTimeRange, DateTimeRange, getBaseFieldName, isRangeFilter } from 'src/components/Contacts/types';
import DateTimeFilter from './DateTimeFilter';
import DateTimeRangeFilter from './DateTimeRangeFilter';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

interface FilterChipProps {
  filter: FilterValue;
  onDelete?: () => void;
  onChange: (value: FilterValueType) => void;
  onClick?: () => void;
  hideDelete?: boolean;
  expanded?: boolean;
  onBlur?: () => void;
}

const scoreRangeOptions = [
  { value: { min: 90 }, label: '90+' },
  { value: { min: 80, max: 89 }, label: '80-89' },
  { value: { min: 70, max: 79 }, label: '70-79' },
  { value: { min: 60, max: 69 }, label: '60-69' },
  { value: { max: 59 }, label: 'Below 60' },
];

export const FilterChip: React.FC<FilterChipProps> = ({
  filter,
  onDelete,
  onChange,
  onClick,
  hideDelete = false,
  expanded = false,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(expanded);
  const theme = useTheme();

  const getChipColor = () => {
    switch (filter.option.group) {
      case 'Details':
        return 'primary';
      case 'Company':
        return 'success';
      case 'Activity':
        return 'info';
      case 'Advanced':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatValue = (value: FilterValueType): string => {
    if (Array.isArray(value)) {
      return value.length > 1 ? `${value.length} items` : value[0] || '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (isScoreRange(value)) {
      if (value.min !== undefined && value.max !== undefined) {
        return `${value.min}-${value.max}`;
      } else if (value.min !== undefined) {
        return `${value.min}+`;
      } else if (value.max !== undefined) {
        return `≤${value.max}`;
      }
    }
    if (isDate(value)) {
      return value.toLocaleDateString();
    }
    if (isDateTimeRange(value)) {
      const { mode, start, end } = value;
      if (mode === 'before' && end) {
        return `before ${end.toLocaleDateString()}`;
      } else if (mode === 'after' && start) {
        return `after ${start.toLocaleDateString()}`;
      } else if (mode === 'between' && start && end) {
        return `${start.toLocaleDateString()}-${end.toLocaleDateString()}`;
      }
      return mode;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const str = value.toString();
      return str.length > 20 ? `${str.substring(0, 20)}...` : str;
    }
    return '';
  };

  // Handle datetime range filters
  if (isRangeFilter(filter.option.id) && isExpanded) {
    const baseFieldName = getBaseFieldName(filter.option.id);
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                {filter.option.label}
              </Typography>
              {filter.value && (
                <Typography component="span" variant="body2" sx={{ opacity: 0.8, fontWeight: 400 }}>
                  : {formatValue(filter.value)}
                </Typography>
              )}
            </Box>
          }
          onDelete={!hideDelete ? onDelete : undefined}
          onClick={() => setIsExpanded(false)}
          deleteIcon={<ExpandLess />}
          color={getChipColor()}
          sx={{ cursor: 'pointer', maxWidth: '280px' }}
        />

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1300,
              mt: 0.5,
              minWidth: 400,
              maxWidth: 500,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            }}
          >
            <DateTimeRangeFilter
              fieldType={baseFieldName}
              value={isDateTimeRange(filter.value) ? filter.value : undefined}
              onChange={(range) => onChange(range || '')}
            />
          </Box>
        </Collapse>
      </Box>
    );
  }

  // Default filter display when not expanded
  if (!isExpanded) {
    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
              {filter.option.label}
            </Typography>
            {filter.value && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  opacity: 0.8,
                  maxWidth: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                }}
              >
                : {formatValue(filter.value)}
              </Typography>
            )}
          </Box>
        }
        onDelete={!hideDelete ? onDelete : undefined}
        onClick={() => setIsExpanded(true)}
        color={getChipColor()}
        sx={{
          cursor: 'pointer',
          maxWidth: '280px',
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          transition: 'all 0.2s ease',
        }}
      />
    );
  }

  // Expanded view for other filter types
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        borderColor: 'divider',
        minWidth: 200,
      }}
    >
      {/* Boolean filters */}
      {typeof filter.value === 'boolean' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{filter.value ? 'Yes' : 'No'}</Typography>
          <Switch
            checked={filter.value}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
          />
        </Box>
      )}

      {/* Score range filters */}
      {isScoreRange(filter.value) && (
        <FormControl fullWidth size="small">
          <Select
            value={JSON.stringify(filter.value) || ''}
            onChange={(e) => {
              if (e.target.value) {
                const selectedRange = JSON.parse(e.target.value as string);
                onChange(selectedRange);
              } else {
                onChange('');
              }
            }}
            displayEmpty
          >
            <MenuItem value="">Select Range</MenuItem>
            {scoreRangeOptions.map((option, index) => (
              <MenuItem key={index} value={JSON.stringify(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Date filters */}
      {isDate(filter.value) && (
        <DatePicker
          value={filter.value}
          onChange={(newValue) => onChange(newValue || new Date())}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
            },
          }}
        />
      )}

      {/* Text input for other types */}
      {!isDate(filter.value) && !isScoreRange(filter.value) && typeof filter.value !== 'boolean' && (
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={`Enter ${filter.option.label.toLowerCase()}`}
          value={typeof filter.value === 'string' || typeof filter.value === 'number' ? filter.value : ''}
          onChange={(e) => {
            const newValue = e.target.value;
            if (['company_size', 'score'].includes(filter.option.id)) {
              const numValue = parseInt(newValue);
              onChange(isNaN(numValue) ? '' : numValue);
            } else {
              onChange(newValue);
            }
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              setIsExpanded(false);
            }
          }}
          placeholder={`Enter ${filter.option.label.toLowerCase()}`}
        />
      )}
    </Paper>
  );
};

export default FilterChip; 
